import React, { useState, useEffect } from "react";
import "./EmployeeDashboard.css";
import "./AdminDashboard.css";
import { useNavigate } from "react-router-dom";
import fetchBase from "../utils/fetchBase";
import OuraNav from "../components/OuraNav";
import LoadingOverlay from "../components/LoadingOverlay";
import SignaturePad from "../components/SignaturePad";

// ⏱️ Duration display for live shift tracking
function LiveDuration({ startTime }) {
    const [elapsed, setElapsed] = useState("");

    useEffect(() => {
        if (!startTime) return;

        const interval = setInterval(() => {
            const now = new Date();


            const diff = now - new Date(startTime);
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff / (1000 * 60)) % 60);
            const seconds = Math.floor((diff / 1000) % 60);

            const pad = (n) => n.toString().padStart(2, "0");
            setElapsed(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
        }, 1000);

        return () => clearInterval(interval);
    }, [startTime]);

    return <span>{elapsed}</span>;
}

function haversineDistance(lat1, lon1, lat2, lon2) {
    function toRad(x) {
        return x * Math.PI / 180;
    }

    const R = 6371; // Radius of Earth in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
}

function formatShiftDateLabel(dateStr) {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    const dayLabel = date.getTime() === today.getTime()
        ? "Today"
        : date.toLocaleDateString(undefined, { weekday: "short" });

    const readableDate = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

    return `${dayLabel}, ${readableDate}`;
}

// 🧠 Core logic of employee dashboard and shift handling
export default function EmployeeDashboard() {

    const [isClockedIn, setIsClockedIn] = useState(false);
    const [clockInTime, setClockInTime] = useState(null);
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [showLatePrompt, setShowLatePrompt] = useState(false);
    const [scheduledEndTime, setScheduledEndTime] = useState(null);
    const [clockInMessage, setClockInMessage] = useState("");
    const [upcomingSchedules, setUpcomingSchedules] = useState([]);
    const [employeeName, setEmployeeName] = useState("");
    const [overlay, setOverlay] = useState(null);
    const [bellOpen, setBellOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [bellNotifications, setBellNotifications] = useState([]);
    const [dynamicAssignments, setDynamicAssignments] = useState([]);
    const [pendingInitialTimeLogId, setPendingInitialTimeLogId] = useState(null);
    const [pendingFinalTimesheetId, setPendingFinalTimesheetId] = useState(null);
    const [finalSignatureStage, setFinalSignatureStage] = useState(null); // "veteran" | "employee" | null
    const [pendingVeteranFinalSignature, setPendingVeteranFinalSignature] = useState(null);
    const [clockInConfirm, setClockInConfirm] = useState(null);

        useEffect(() => {
            const authHeader = "Bearer " + localStorage.getItem("jwt_token");
            fetch(`${fetchBase}/api/document-hub/employee/my-hub`, {
                headers: { Authorization: authHeader }
            })
                .then(res => res.json())
                .then(data => {
                    const allDocs = (data.sections || []).flatMap(s => s.documents || []);
                    setDynamicAssignments(allDocs.filter(d => d.isDynamic && d.status !== "COMPLETED"));
                })
                .catch(err => console.error("Failed to fetch dynamic assignments:", err));
        }, []);

    useEffect(() => {
        const authHeader = "Bearer " + localStorage.getItem("jwt_token");
        fetch(`${fetchBase}/api/notifications/unread-count`, {
            headers: { Authorization: authHeader }
        })
            .then(res => res.json())
            .then(data => {
                if (data && typeof data.count === "number") setUnreadCount(data.count);
            })
            .catch(err => console.error("Failed to fetch unread count:", err));
    }, []);

    useEffect(() => {
        const storedTime = localStorage.getItem("clockInTime");
        const storedMessage = localStorage.getItem("clockInMessage");
        const phone = localStorage.getItem("employee_phone");
        const authHeader = "Bearer " + localStorage.getItem("jwt_token");

        // 🔍 Fetch upcoming schedules
                fetch(`${fetchBase}/api/schedule/employee-upcoming?phone=${encodeURIComponent(phone)}`, {
                    headers: { Authorization: authHeader }
                })
                    .then(res => res.json())
                    .then(data => {
                        if (Array.isArray(data)) setUpcomingSchedules(data);
                    })
                    .catch(err => console.error("Error fetching schedules:", err));

                // 🔍 Fetch employee name
                fetch(`${fetchBase}/api/employee/by-phone/${encodeURIComponent(phone)}`, {
            headers: { Authorization: authHeader }
        })
            .then(res => res.json())
            .then(data => {
                if (data?.firstName) {
                    setEmployeeName(data.firstName);
                } else if (data?.fullName) {
                    setEmployeeName(data.fullName.split(" ")[0]);
                }
            });

                // 💾 Check backend for an open shift (covers admin-backfilled clock-ins too)
                fetch(`${fetchBase}/time-log/status?phone=${encodeURIComponent(phone)}`, {
                    headers: { Authorization: authHeader }
                })
                    .then(res => res.json())
                    .then(statusData => {
                        if (statusData.clockedIn) {
                            localStorage.setItem("clockInTime", statusData.clockInTime);
                            localStorage.setItem("clockInMessage", statusData.message);
                            setIsClockedIn(true);
                            setClockInTime(new Date(statusData.clockInTime));
                            setClockInMessage(statusData.message);
                        } else if (storedTime) {
                            // Stale localStorage from a shift that's already been closed elsewhere
                            localStorage.removeItem("clockInTime");
                            localStorage.removeItem("clockInMessage");
                        }
                    })
                    .catch(err => console.error("Failed to check clock-in status:", err));

                if (storedTime) {
                    setIsClockedIn(true);
                    const clockIn = new Date(storedTime);
                    setClockInTime(clockIn);

                    if (storedMessage) {
                        setClockInMessage(storedMessage);
                    }

            fetch(`${fetchBase}/api/schedule/today-by-phone/${encodeURIComponent(phone)}`, {
                headers: { Authorization: authHeader }
            })
                .then(res => res.json())
                .then(data => {
                    if (data?.endTime) {
                        const currentTime = new Date();
                        const [h, m, s] = data.endTime.split(":");

                        let endTime = new Date(
                            currentTime.getFullYear(),
                            currentTime.getMonth(),
                            currentTime.getDate(),
                            parseInt(h),
                            parseInt(m),
                            parseInt(s || 0)
                        );

                        // If end time is before now and shift crosses midnight, add one day
                        if (endTime < new Date()) {
                            endTime = new Date(endTime.getTime() + 24 * 60 * 60 * 1000);
                        }

                        setScheduledEndTime(endTime);

                        const now = new Date();
                        const thirtyMinsAfterEnd = new Date(endTime.getTime() + 30 * 60 * 1000);

                        if (now > thirtyMinsAfterEnd) {
                            setShowLatePrompt(true);
                        }
                    }
                })
                .catch(err => console.error("Error fetching schedule:", err));
        }
    }, []);

    const handleClockIn = async () => {
        const phone = localStorage.getItem("employee_phone");
        const authHeader = "Bearer " + localStorage.getItem("jwt_token");

            if ('geolocation' in navigator) {
                let latitude, longitude;
                let usedFallbackLocation = false;

                try {
                    // Wrap geolocation in a promise for async/await
                    const position = await new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
                    });

                    latitude = position.coords.latitude;
                    longitude = position.coords.longitude;
                    console.log("📍 Fetched employee location:", latitude, longitude);

                } catch (err) {
                    console.warn("📍 Location access failed, using last known location for testing");
                    usedFallbackLocation = true;

                    // Try to get last known location from localStorage
                    const storedLat = localStorage.getItem("geo_lat");
                    const storedLng = localStorage.getItem("geo_lng");

                    if (storedLat && storedLng) {
                        latitude = parseFloat(storedLat);
                        longitude = parseFloat(storedLng);
                        console.log("📍 Using stored location:", latitude, longitude);
                    } else {
                        // Default location for testing (use your test coordinates)
                        latitude = 33.3027;
                        longitude = -96.5469;
                        console.log("📍 Using default test location:", latitude, longitude);
                    }
                }

            localStorage.setItem("geo_lat", latitude.toString());
            localStorage.setItem("geo_lng", longitude.toString());



                // Fetch client location outside of callback
                const locationResponse = await fetch(`${fetchBase}/api/schedule/today-by-phone/${encodeURIComponent(phone)}`, {
                    method: "GET",
                    headers: { Authorization: authHeader },
                });

                const clientLocation = await locationResponse.json();

                if (!clientLocation || !clientLocation.latitude || !clientLocation.longitude || !clientLocation.startTime) {
                    setOverlay({ message: "You do not have a scheduled shift today.", status: "error" });
                    setTimeout(() => setOverlay(null), 2500);
                    return;
                }

                // Check distance and start time
                const dist = haversineDistance(latitude, longitude, clientLocation.latitude, clientLocation.longitude);
                const today = new Date();
                const [hours, minutes] = clientLocation.startTime.split(":");

                const scheduledStart = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate(),
                    parseInt(hours),
                    parseInt(minutes)
                );
                    const twentyFiveMinsBefore = new Date(scheduledStart.getTime() - 25 * 60 * 1000);

                    if (new Date() < twentyFiveMinsBefore) {
                    setOverlay({ message: "⏰ You are too early to clock in.", status: "error" });
                    setTimeout(() => setOverlay(null), 2500);
                    return;
                }

                    if (dist >= 0.1) {
                        const message = usedFallbackLocation
                            ? "⚠️ We couldn't get your device's location. Please check your location settings and try again."
                            : "📍 You are too far from the client location to clock in.";
                        setOverlay({ message, status: "error" });
                        setTimeout(() => setOverlay(null), 2500);
                        return;
                    }

                // ✅ First, check if already clocked in for today's scheduled shift
                try {
                    const scheduleCheckRes = await fetch(`${fetchBase}/time-log/exists-today?phone=${encodeURIComponent(phone)}`, {
                        headers: { Authorization: authHeader },
                    });
                    const scheduleCheck = await scheduleCheckRes.json();

                    if (scheduleCheck.exists) {
                        setOverlay({ message: "⚠ You are already clocked in for today.", status: "error" });
                        setTimeout(() => setOverlay(null), 2500);
                        return;
                    }
                } catch (err) {
                    console.error("Error checking existing time log:", err);
                    setOverlay({ message: "Error verifying shift status. Please try again.", status: "error" });
                    setTimeout(() => setOverlay(null), 2500);
                    return;
                }

                    // Preview the matching shift before actually clocking in
                    const previewRes = await fetch(`${fetchBase}/time-log/clock-in-preview?phone=${encodeURIComponent(phone)}`, {
                        headers: { Authorization: authHeader },
                    });
                    const preview = await previewRes.json();

                    if (preview.status !== "ok") {
                        setOverlay({ message: preview.message || "No scheduled shift found.", status: "error" });
                        setTimeout(() => setOverlay(null), 2500);
                        return;
                    }

                    setClockInConfirm({ preview, latitude, longitude, phone, authHeader });
                    return;

           // } catch (err) {
           //     console.error("Clock-in failed:", err);
           //     alert("Location access is required to clock in or request timed out.");
           // }

        } else {
            setOverlay({ message: "Geolocation is not supported by this browser.", status: "error" });
            setTimeout(() => setOverlay(null), 2500);
        }
    };

            const confirmClockIn = async () => {
                if (!clockInConfirm) return;
                const { latitude, longitude, phone, authHeader } = clockInConfirm;
                setClockInConfirm(null);

                const response = await fetch(`${fetchBase}/time-log/clock-in`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                    body: JSON.stringify({ phone, latitude, longitude }),
                });

                const data = await response.json();
                if (data.status === "success") {
                    const now = new Date();
                    localStorage.setItem("clockInTime", now.toISOString());
                    localStorage.setItem("clockInMessage", data.message);
                    setClockInTime(now);
                    setIsClockedIn(true);
                    setClockInMessage(data.message);
                } else {
                    setOverlay({ message: data.message, status: "error" });
                    setTimeout(() => setOverlay(null), 2500);
                }
            };

            const handleClockOut = async () => {
            const phone = localStorage.getItem("employee_phone");
            const authHeader = "Bearer " + localStorage.getItem("jwt_token");

            try {
                const response = await fetch(`${fetchBase}/time-log/clock-out?phone=${phone}`, {
                    method: "POST",
                    headers: { Authorization: authHeader },
                });

                const data = await response.json();

                if (response.ok) {
                    localStorage.removeItem("clockInTime");
                    setIsClockedIn(false);
                    setClockInTime(null);
                    setUpcomingSchedules(prev =>
                        prev.filter((_, index) => index !== 0)
                    );
                    setOverlay({ message: data.message, status: "success" });
                    setTimeout(() => setOverlay(null), 2500);

                    if (data.timeLogId) {
                        setPendingInitialTimeLogId(data.timeLogId);
                        setPendingFinalTimesheetId(data.isLastDay ? data.timesheetId : null);
                    }
                } else {
                    setOverlay({ message: data.message, status: "error" });
                    setTimeout(() => setOverlay(null), 2500);
                }
                        } catch (err) {
                            console.error(err);
                            setOverlay({ message: "Clock-out failed.", status: "error" });
                            setTimeout(() => setOverlay(null), 2500);
                        }
                    };

                        const handleSaveVeteranInitial = async (dataUrl) => {
                            const authHeader = "Bearer " + localStorage.getItem("jwt_token");
                            setOverlay({ message: "Clocking out...", status: "" });
                            try {
                                const res = await fetch(`${fetchBase}/time-log/${pendingInitialTimeLogId}/veteran-initial`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json", Authorization: authHeader },
                                    body: JSON.stringify({ veteranInitial: dataUrl })
                                });
                                if (res.ok) {
                                    if (pendingFinalTimesheetId) {
                                        setOverlay(null);
                                    } else {
                                        setOverlay({ message: "✅ Clock-out successful.", status: "success" });
                                    }
                                } else {
                                    setOverlay({ message: "❌ Failed to save initial.", status: "error" });
                                }
                            } catch (err) {
                                console.error("Failed to save veteran initial:", err);
                                setOverlay({ message: "❌ Failed to save initial.", status: "error" });
                            } finally {
                                setPendingInitialTimeLogId(null);
                                if (pendingFinalTimesheetId) {
                                    setFinalSignatureStage("veteran");
                                } else {
                                    setTimeout(() => setOverlay(null), 2500);
                                }
                            }
                        };

                        const handleSaveVeteranFinalSignature = (dataUrl) => {
                            setPendingVeteranFinalSignature(dataUrl);
                            setFinalSignatureStage("employee");
                        };

                        const handleSaveEmployeeSignature = async (dataUrl) => {
                            const authHeader = "Bearer " + localStorage.getItem("jwt_token");
                            setOverlay({ message: "Saving timesheet...", status: "" });
                            try {
                                await fetch(`${fetchBase}/time-log/timesheet/${pendingFinalTimesheetId}/final-signature`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json", Authorization: authHeader },
                                    body: JSON.stringify({
                                        veteranFinalSignature: pendingVeteranFinalSignature,
                                        employeeSignature: dataUrl
                                    })
                                });
                                setOverlay({ message: "✅ Timesheet completed.", status: "success" });
                            } catch (err) {
                                console.error("Failed to save final signatures:", err);
                                setOverlay({ message: "❌ Failed to save signatures.", status: "error" });
                            } finally {
                                setFinalSignatureStage(null);
                                setPendingFinalTimesheetId(null);
                                setPendingVeteranFinalSignature(null);
                                setTimeout(() => setOverlay(null), 2500);
                            }
                        };

                return (
        <>
            {overlay && <LoadingOverlay message={overlay.message} status={overlay.status} />}
            {clockInConfirm && (
                <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
                    <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", width: "320px" }}>
                        <p style={{ fontWeight: 700, fontSize: "15px", margin: "0 0 12px", color: "#1d1d1f" }}>Confirm Clock In</p>
                        <p style={{ fontSize: "13px", color: "#1d1d1f", margin: "0 0 16px", lineHeight: 1.5 }}>
                            You are scheduled to work {clockInConfirm.preview.durationHours} hours at {clockInConfirm.preview.clientName}, from {clockInConfirm.preview.scheduledStart} to {clockInConfirm.preview.scheduledEnd}.
                            If you clock in now, this shift will end at {clockInConfirm.preview.computedClockOutIfNow}.
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <button onClick={() => setClockInConfirm(null)}
                                style={{ background: "#f5f5f7", color: "#1d1d1f", border: "none", borderRadius: "8px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                                Cancel
                            </button>
                            <button onClick={confirmClockIn}
                                style={{ background: "#0071e3", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
                {pendingInitialTimeLogId && (
                    <SignaturePad
                        label="Veteran Initial"
                        onSave={handleSaveVeteranInitial}
                        note="Your recorded hours reflect your scheduled shift time, not your actual clock-out time."
                    />
                )}
            {finalSignatureStage === "veteran" && (
                <SignaturePad label="Veteran Final Signature" onSave={handleSaveVeteranFinalSignature} saveLabel="Next" />
            )}
            {finalSignatureStage === "employee" && (
                <SignaturePad label="Employee Signature" onSave={handleSaveEmployeeSignature} />
            )}
            <nav className="apple-global-nav">

        <div className="left-nav">
            <img src="/images/logoPlaceholder1.png" alt="Logo" className="apple-logo"/>
        </div>

        <div className="center-nav">
            <a href="/employee/dashboard">Dashboard</a>
        </div>

        <div className="notification-bell-container" style={{ position: "relative", marginRight: "16px", cursor: "pointer" }} onClick={() => navigate("/employee/notifications")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6e6e73" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                    <span style={{
                        position: "absolute", top: "-6px", right: "-8px",
                        background: "#ff3b30", color: "white", fontSize: "10px",
                        fontWeight: "700", borderRadius: "10px", padding: "1px 5px",
                        minWidth: "14px", textAlign: "center"
                    }}>
                        {unreadCount}
                    </span>
                )}
            </div>

            <div className="account-dropdown-container">
                <span className="account-link">Account ▾</span>
            <div className="account-dropdown">
                <a href="/employee/history">History</a>
                <a href="/employee/manual">Manual</a>
                <a href="/logout">Log Out</a>
            </div>
        </div>

        <button
            className={`hamburger-btn ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
        >
            <span className="bar"></span>
            <span className="bar"></span>
        </button>

    </nav>

    {menuOpen && (
        <div className="admin-mobile-menu">
            <a href="/employee/dashboard" onClick={() => setMenuOpen(false)}>Dashboard</a>
            <a href="/employee/history" onClick={() => setMenuOpen(false)}>History</a>
            <a href="/employee/document-hub" onClick={() => setMenuOpen(false)}>Document Hub</a>
            <a href="/employee/timesheets" onClick={() => setMenuOpen(false)}>Timesheets</a>
            <a href="/employee/manual" onClick={() => setMenuOpen(false)}>Manual</a>
            <a href="/logout" onClick={() => setMenuOpen(false)}>Log Out</a>
        </div>
    )}


            {showLatePrompt && (
                <div className="late-prompt-overlay">
                    <div className="late-prompt-modal">
                        <h2>You’re 30+ minutes past your shift end time.</h2>
                        <p>Would you like to clock out now?</p>
                        <div className="prompt-buttons">
                            <button className="yes-btn" onClick={handleClockOut}>Yes, clock out</button>
                            <button className="no-btn" onClick={() => setShowLatePrompt(false)}>No</button>
                        </div>
                    </div>
                </div>
            )}

                <div className="employee-dashboard-modern">
                    <div className="dashboard-fixed-header">
                        <div className="dashboard-header">
                            <h1>Welcome {employeeName || "!"}</h1>
                            <p className="dashboard-date">{new Date().toDateString()}</p>
                            <p className="dashboard-location">
                                {isClockedIn ? clockInMessage : "Your location will be verified on clock-in"}
                            </p>

                            <div className={`clocked-in-tile ${isClockedIn ? "slide-in" : ""}`}>
                                {!isClockedIn && !clockInTime ? (
                                    <button className="clock-in-btn" onClick={handleClockIn}>Clock In</button>
                                ) : (
                                    <>
                                        <div className="shift-left-pane">
                                            <div className="live-duration-wrapper">
                                                <LiveDuration startTime={clockInTime}/>
                                            </div>
                                        </div>
                                        <button className="clock-out-btn slide-right" onClick={handleClockOut}>
                                            Clock Out
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-scrollable-section">
                        <div className="dashboard-section">
                            <div className="dashboard-card">
                                <h3>Your schedule</h3>
                                {upcomingSchedules.length === 0 ? (
                                    <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>No upcoming shifts scheduled.</p>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                        {upcomingSchedules.map((shift, index) => {
                                            const today = new Date().toISOString().split("T")[0];
                                            const isToday = shift.date === today;
                                            const now = new Date();
                                            const [startH, startM] = shift.startTime.split(/[: ]/);
                                            const [endH, endM] = shift.endTime.split(/[: ]/);
                                            const isActive = isToday && isClockedIn;

                                            return (
                                                <div key={index} style={{
                                                    background: "var(--color-background-primary)",
                                                    border: "0.5px solid var(--color-border-tertiary)",
                                                    borderRadius: "12px",
                                                    padding: "14px 16px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "14px"
                                                }}>
                                                    <div style={{
                                                        width: "4px",
                                                        borderRadius: "4px",
                                                        alignSelf: "stretch",
                                                        minHeight: "48px",
                                                        flexShrink: 0,
                                                        background: shift.clientColor || "#888"
                                                    }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <p style={{ fontWeight: 500, fontSize: "15px", margin: "0 0 2px 0", color: "var(--color-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                            {shift.clientName}
                                                        </p>
                                                            <p style={{ fontSize: "13px", color: "var(--color-text-secondary)", margin: "0 0 2px 0" }}>
                                                                {shift.startTime} — {shift.endTime}
                                                            </p>
                                                            {shift.clientAddress && (
                                                                <p style={{ fontSize: "12px", color: "var(--color-text-tertiary)", margin: "0 0 4px 0" }}>
                                                                    {shift.clientAddress}
                                                                </p>
                                                            )}
                                                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                                            <span style={{
                                                                fontSize: "11px",
                                                                padding: "2px 8px",
                                                                borderRadius: "20px",
                                                                fontWeight: 500,
                                                                background: isActive ? "#EAF3DE" : "#F1EFE8",
                                                                color: isActive ? "#27500A" : "#444441"
                                                            }}>
                                                                {isActive ? "Clocked in" : formatShiftDateLabel(shift.date)}
                                                            </span>
                                                            {isActive && (
                                                                <span style={{
                                                                    fontSize: "11px",
                                                                    padding: "2px 8px",
                                                                    borderRadius: "20px",
                                                                    fontWeight: 500,
                                                                    background: "#F1EFE8",
                                                                    color: "#444441"
                                                                }}>
                                                                    {formatShiftDateLabel(shift.date)}
                                                                </span>
                                                            )}
                                                            <span style={{ fontSize: "11px", color: "var(--color-text-tertiary)" }}>
                                                                {shift.serviceCode}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span style={{ fontSize: "13px", color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>
                                                        {shift.shiftHours}h
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                                        </div>
                            <div className="dashboard-card">
                            <h3>Dynamic Assignments</h3>
                            {dynamicAssignments.length === 0 ? (
                                <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>No dynamic assignments yet.</p>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    {dynamicAssignments.map((doc) => (
                                        <div
                                            key={doc.id}
                                            onClick={() => navigate(`/employee/document-hub/documents/${doc.id}`)}
                                            style={{
                                                background: "var(--color-background-primary)",
                                                border: "0.5px solid var(--color-border-tertiary)",
                                                borderRadius: "12px",
                                                padding: "14px 16px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "14px",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <div style={{
                                                width: "4px",
                                                borderRadius: "4px",
                                                alignSelf: "stretch",
                                                minHeight: "48px",
                                                flexShrink: 0,
                                                background: doc.status === "COMPLETED" ? "#34c759" : "#ff9500"
                                            }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ fontWeight: 500, fontSize: "15px", margin: "0 0 2px 0", color: "var(--color-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {doc.assignmentTitle}
                                                </p>
                                                <p style={{ fontSize: "13px", color: "var(--color-text-secondary)", margin: "0 0 2px 0" }}>
                                                    Deadline: {doc.deadline ? new Date(doc.deadline + "T12:00:00").toLocaleDateString() : "—"}
                                                </p>
                                                <p style={{ fontSize: "12px", color: "var(--color-text-tertiary)", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {doc.filename}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                                <div className="dashboard-card"><h3>Announcements</h3>
                                    <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>No announcements yet.</p>
                                </div>
                        </div>
                    </div>
                </div>
        </>
    );
}